import { formatDistance, parseISO } from 'date-fns';
import { InspectionDetailLevel, InspectionOnlineStatus, InspectionStatus, SensorType, TemperatureUnit, } from '../types';
import { convertTemp, global } from '.';
import { getUserPreferenceTemperatureUnit } from './temperature';
import { InspectionReportKey } from '../types/Inspection';
export const determineInspectionStatus = (data) => {
    if (!data) {
        return {
            status: InspectionStatus.ERROR,
            statusCount: 0,
        };
    }
    const errorsAndWarnings = checkFieldsErrorsAndWarnings(data);
    return convertErrorToInspectionStatus(errorsAndWarnings);
};
const convertErrorToInspectionStatus = errorsAndWarnings => errorsAndWarnings.hasError
    ? {
        status: InspectionStatus.ERROR,
        statusCount: errorsAndWarnings.errorCount,
    }
    : errorsAndWarnings.hasWarning
        ? {
            status: InspectionStatus.WARNING,
            statusCount: errorsAndWarnings.warningCount,
        }
        : {
            status: InspectionStatus.GOOD,
            statusCount: 0,
        };
export const getTitle = (data) => {
    var _a;
    return typeof data.title === 'string'
        ? data.title
        : (_a = data.title) === null || _a === void 0 ? void 0 : _a.field;
};
export const getId = (data) => {
    var _a;
    return typeof data.id === 'number'
        ? data.id
        : (_a = data.id) === null || _a === void 0 ? void 0 : _a.field;
};
export const getSensorTypes = (data) => {
    var _a;
    let sensorTypes = (((_a = data.type) === null || _a === void 0 ? void 0 : _a.field) ||
        []);
    if ((sensorTypes === null || sensorTypes === void 0 ? void 0 : sensorTypes.length) === 2 &&
        sensorTypes.includes(SensorType.TEMPERATURE) &&
        sensorTypes.includes(SensorType.HUMIDITY)) {
        // ['TEMPERATURE', 'HUMIDITY'] = ambient sensor
        sensorTypes = ['AMBIENT'];
    }
    return sensorTypes;
};
export const getSummaryFields = (data, link) => {
    const status = data.status;
    const last_check_in = data.last_check_in;
    const isNeverOnline = (last_check_in === null || last_check_in === void 0 ? void 0 : last_check_in.field) &&
        new Date(last_check_in === null || last_check_in === void 0 ? void 0 : last_check_in.field).getTime() === 0;
    return Object.assign(Object.assign({}, determineInspectionStatus(data)), { onlineStatus: InspectionOnlineStatus[status === null || status === void 0 ? void 0 : status.field], onlineStatusTime: isNeverOnline
            ? 'Never online'
            : (last_check_in === null || last_check_in === void 0 ? void 0 : last_check_in.field)
                ? getRelativeTime(parseISO(last_check_in.field))
                : '', onlineStatusError: status === null || status === void 0 ? void 0 : status.error, link });
};
export const getSensorsOrActuatorsSummaryFields = (data, options = {}) => {
    var _a;
    const sensors = data.filter(sensor => sensor);
    if (!options.isUnpaired && !sensors.length) {
        return {};
    }
    if ((_a = options.statisticsData) === null || _a === void 0 ? void 0 : _a.result) {
        return determineInspectionStatus(options.statisticsData);
    }
    const errorsAndWarnings = sensors
        .map(sensor => checkFieldsErrorsAndWarnings(sensor))
        .reduce((acc, value) => ({
        hasError: acc.hasError || value.hasError,
        hasWarning: acc.hasWarning || value.hasWarning,
        errorCount: acc.errorCount + value.errorCount,
        warningCount: acc.warningCount + value.warningCount,
    }), {
        hasError: false,
        hasWarning: false,
        errorCount: 0,
        warningCount: 0,
    });
    return convertErrorToInspectionStatus(errorsAndWarnings);
};
const inspectionNetworkGroupKeys = [
    InspectionReportKey.EDGE_CONTROLLERS,
    InspectionReportKey.SUPER_EDGE_CONTROLLERS,
];
const inspectionDeviceKeys = [
    InspectionReportKey.HUBS,
    InspectionReportKey.SUPERHUB_SENSORS,
    InspectionReportKey.VENSTAR_THERMOSTATS,
    InspectionReportKey.EB_THERMOSTATS,
    InspectionReportKey.SITE_CONTROLLERS,
    InspectionReportKey.ENERGY_PROS,
    InspectionReportKey.ENERGY_PRO2S,
];
export const getProcessedInspectionData = (data) => {
    const networkGroupInspectionData = {};
    const devicesWithoutNWG = {};
    inspectionNetworkGroupKeys.forEach(key => {
        data[key] &&
            data[key].forEach((edgeController) => {
                const id = edgeController.id.field;
                networkGroupInspectionData[id] = {};
                networkGroupInspectionData[id][key] = [edgeController];
            });
    });
    inspectionDeviceKeys.forEach(key => {
        const devices = data[key];
        devices &&
            Array.isArray(devices) ?
            devices.forEach((device) => {
                const networkGroupId = device.networkgroup_id.field;
                if (networkGroupId in networkGroupInspectionData) {
                    if (networkGroupInspectionData[networkGroupId][key]) {
                        networkGroupInspectionData[networkGroupId][key].push(device);
                    }
                    else {
                        networkGroupInspectionData[networkGroupId][key] = [device];
                    }
                }
                else {
                    if (devicesWithoutNWG[key]) {
                        devicesWithoutNWG[key].push(device);
                    }
                    else {
                        devicesWithoutNWG[key] = [device];
                    }
                }
            }) : devicesWithoutNWG[key] = [devices];
    });
    if (data.unpaired_sensors)
        devicesWithoutNWG.unpaired_sensors = data.unpaired_sensors;
    return {
        networkGroupInspectionData,
        devicesWithoutNWG,
    };
};
export const getDetailFields = (fields, data, detailLevel = InspectionDetailLevel.ALL) => {
    return fields
        .filter(field => !!field.key || field.isHr)
        .map(field => {
        let value;
        const rawFieldObject = (data[field.key] || {});
        if (typeof rawFieldObject === 'object' && 'field' in rawFieldObject) {
            // simple value
            value = field.transformValue
                ? field.transformValue(rawFieldObject.field)
                : rawFieldObject.field !== null
                    ? rawFieldObject.field
                    : global.NOT_AVAILABLE;
            if (detailLevel === InspectionDetailLevel.ISSUES &&
                !rawFieldObject.error &&
                !rawFieldObject.warning) {
                return {
                    key: null,
                };
            }
        }
        else {
            // table or null
            value = '';
            if (detailLevel === InspectionDetailLevel.ISSUES) {
                return {
                    key: null,
                };
            }
        }
        return Object.assign(Object.assign({}, field), { value, error: rawFieldObject.error, warning: rawFieldObject.warning });
    })
        .filter(field => field.key !== null);
};
export const getRelativeTime = (time, referenceTime = new Date()) => {
    try {
        if (Math.abs(time.getTime() - referenceTime.getTime()) < 60 * 1000) {
            // within a minute
            return 'just now';
        }
        return formatDistance(time, referenceTime, { addSuffix: true });
    }
    catch (ex) {
        return global.NOT_AVAILABLE;
    }
};
export const transformDhcpStatusValue = value => value === 'DHCP' ? 'Enable' : 'Disable';
export const transformTemperatureUnit = (payload) => {
    switch (payload) {
        case 'CELSIUS':
            return TemperatureUnit.C;
        case 'FAHRENHEIT':
            return TemperatureUnit.F;
        case null:
            return payload;
    }
};
export const transformTemperatureValue = (value, currentUser, valueUnit = TemperatureUnit.C
// Energybox Sensor always return celcius while
// venstar thermostat comes with temperature scale setting
// null implies error so return NOT_AVAILABLE even if value is not null
) => {
    if (value === null || isNaN(Number(value)) || valueUnit === null) {
        return global.NOT_AVAILABLE;
    }
    const targetUnit = getUserPreferenceTemperatureUnit(currentUser);
    const convertedValue = valueUnit === targetUnit
        ? value
        : convertTemp(value, valueUnit === TemperatureUnit.F ? 'f' : 'c', 1);
    return `${convertedValue} ${targetUnit}`;
};
export const transformDoorValue = value => (value ? 'Open' : 'Closed');
export const transformBusDeviceInfoValue = value => (value === null || value === void 0 ? void 0 : value.length) > 0 ? value.join(' - ') : global.NOT_AVAILABLE;
export const checkFieldsErrorsAndWarnings = (data) => {
    if (data.result) {
        return {
            hasError: data.errors > 0,
            hasWarning: data.warnings > 0,
            errorCount: data.errors,
            warningCount: data.warnings,
        };
    }
    // loop below is avoided if API already provides the general error/warning status
    let errorCount = 0;
    let warningCount = 0;
    Object.keys(data).forEach(key => {
        if (Array.isArray(data[key])) {
            const fieldArray = (data[key] || {});
            const error = fieldArray.some(field => field.error);
            const warning = fieldArray.some(field => field.warning);
            errorCount += Number(error);
            warningCount += Number(warning);
        }
        else {
            const rawFieldObject = (data[key] || {});
            if (typeof rawFieldObject === 'object' && 'field' in rawFieldObject) {
                errorCount += !!rawFieldObject.error ? 1 : 0;
                warningCount += !!rawFieldObject.warning ? 1 : 0;
            }
        }
    });
    return {
        hasError: errorCount > 0,
        hasWarning: warningCount > 0,
        errorCount,
        warningCount,
    };
};
export const checkSensorErrorsAndWarnings = (sensor_warnings) => {
    let errorCount = 0;
    let warningCount = 0;
    if (!!sensor_warnings) {
        sensor_warnings.forEach(field => {
            if (field.error)
                errorCount += 1;
            if (field.warning) {
                if (field.warning.startsWith('ERROR_')) {
                    errorCount += 1;
                }
                else {
                    warningCount += 1;
                }
            }
        });
    }
    return {
        hasError: errorCount > 0,
        hasWarning: warningCount > 0,
        errorCount,
        warningCount,
    };
};
export const doesItemContainErrorOrWarning = (item, type) => {
    const { hasError, hasWarning } = checkFieldsErrorsAndWarnings(item);
    if (type === 'error') {
        return hasError;
    }
    if (type === 'warning') {
        return hasWarning;
    }
    return hasError || hasWarning;
};
export const isVersionRequirementFulfilled = (currentVersion, requiredVersion) => {
    if (typeof currentVersion !== 'string' ||
        typeof requiredVersion !== 'string') {
        return false;
    }
    if (currentVersion === requiredVersion) {
        return true;
    }
    const currentVersionArr = currentVersion.split('.');
    const requiredVersionArr = requiredVersion.split('.');
    if (currentVersionArr.length !== requiredVersionArr.length) {
        return false;
    }
    // checking goes from major, minor to patch version numbers
    for (let i = 0; i < currentVersionArr.length; i++) {
        if (Number(currentVersionArr[i]) > Number(requiredVersionArr[i])) {
            return true;
        }
    }
    return false;
};
